import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import {
  getComments, addComment, likeComment, timeAgo, hideComment,
} from "../../api/news";
import { HeartIcon } from "./ReactionIcons";

const MIN_USER_ID = 1;

function Avatar({ author }) {
  // аватары профилей на user-сервисе; best-effort, при ошибке прячем
  if (author?.avatar)
    return <img className="nws-author-noimg" style={{ padding: 0 }} src={`/api-user/assets/profile/${author.avatar}`} alt="" onError={(e) => (e.target.style.display = "none")} />;
  return <span className="nws-author-noimg">{(author?.name || "?")[0].toUpperCase()}</span>;
}

function Comment({ c, onReply, onLike, onHide, isMin }) {
  return (
    <div className="nws-comment">
      <div className="nws-comment-head">
        <Avatar author={c.author} />
        <span className="nws-comment-author">{c.author?.name || "Гость"}</span>
        <span className="nws-comment-time">{timeAgo(c.created_at)}</span>
      </div>
      <div className={`nws-comment-text ${c.hidden ? "hidden" : ""}`}>{c.text}</div>
      {!c.hidden && (
        <div className="nws-comment-actions">
          <button className={`nws-comment-act ${c.is_liked ? "is-liked" : ""}`} onClick={() => onLike(c)}>
            <HeartIcon active={c.is_liked} size={15} /> {c.likes_count > 0 ? c.likes_count : ""}
          </button>
          {onReply && <button className="nws-comment-act" onClick={() => onReply(c)}>Ответить</button>}
          {isMin && <button className="nws-comment-act" onClick={() => onHide(c)}>Скрыть</button>}
        </div>
      )}
    </div>
  );
}

export default function NewsComments({ newsId, onCountChange }) {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const isMin = user?.userId === MIN_USER_ID;

  const [tree, setTree] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [replyTo, setReplyTo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const load = React.useCallback(() => {
    setLoading(true);
    getComments(newsId)
      .then((d) => setTree(d.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [newsId]);

  React.useEffect(() => { load(); }, [load]);

  async function submit() {
    if (!isAuthenticated) { navigate("/login"); return; }
    const t = text.trim();
    // Клиентская валидация — мгновенный фидбек без запроса на сервер
    if (!t) { setErr("Комментарий не может быть пустым"); return; }
    if (t.length < 2) { setErr("Слишком короткий комментарий"); return; }
    if (t.length > 2000) { setErr("Комментарий слишком длинный (максимум 2000)"); return; }
    setBusy(true); setErr(null);
    try {
      await addComment(newsId, t, replyTo?.id || null);
      setText(""); setReplyTo(null);
      load();
      onCountChange?.(+1);
    } catch (e) {
      setErr(e.message || "Не удалось отправить");
    } finally {
      setBusy(false);
    }
  }

  async function onLike(c) {
    if (!isAuthenticated) { navigate("/login"); return; }
    try { await likeComment(c.id); load(); } catch {}
  }
  async function onHide(c) {
    if (!window.confirm("Скрыть этот комментарий?")) return;
    try { await hideComment(c.id); load(); } catch {}
  }

  return (
    <div className="nws-comments">
      <h3>Комментарии</h3>

      <div className="nws-comment-form">
        {replyTo && (
          <div className="nws-comment-time">
            Ответ для {replyTo.author?.name}{" "}
            <button className="nws-comment-act" onClick={() => setReplyTo(null)}>отмена</button>
          </div>
        )}
        <textarea
          placeholder={isAuthenticated ? "Написать комментарий…" : "Войдите, чтобы комментировать"}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onFocus={() => { if (!isAuthenticated) navigate("/login"); }}
          maxLength={3000}
        />
        {err && <div className="nws-err">{err}</div>}
        <button className="nws-btn" disabled={busy || !text.trim()} onClick={submit}>
          {replyTo ? "Ответить" : "Отправить"}
        </button>
      </div>

      {loading ? (
        <div className="nws-loading">Загрузка комментариев…</div>
      ) : tree.length === 0 ? (
        <div className="nws-empty">Пока нет комментариев. Будьте первым!</div>
      ) : (
        tree.map((c) => (
          <div key={c.id}>
            <Comment c={c} onReply={setReplyTo} onLike={onLike} onHide={onHide} isMin={isMin} />
            {c.replies?.length > 0 && (
              <div className="nws-replies">
                {c.replies.map((r) => (
                  <Comment key={r.id} c={r} onReply={setReplyTo} onLike={onLike} onHide={onHide} isMin={isMin} />
                ))}
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );
}
